import Heading from "../Components/Heading/Heading";
import LiContent from "../Components/LiContent/LiContent";
import Line from "../Components/Line/Line";
import RelatedT from "../Components/RelatedArticles/Related";
import ConvertButton from "../Components/ConvertButton";
import React from "react";
import FAQComponent from "../Components/FAQComponent/FAQComponent";


function Safari() {
    const related = [
        {
            href: '/brave',
            name: 'Brave',
            side: 'partial support',
            text: 'Brave has JPEG XL support, but there are still some limitations. We show you how to use it.'
        },
        {
            href: '/chrome',
            name: 'Chrome',
            side: 'partial support',
            text: 'Google introduced JXL support for Chrome, but there are still some limitations. We show you how to use it.'
        },
        {
            href: '/edge',
            name: 'Edge',
            side: 'partial support',
            text: 'Edge is the latest browser from Microsoft. Learn how to use JXL images within Edge.'
        },
        {
            href: '/firefox',
            name: 'Firefox',
            side: 'partial support',
            text: 'Firefox is the largest not-for-profit browser. Learn how to use JXL images within Firefox.'
        },
        {
            href: '/opera',
            name: 'Opera',
            side: 'partial support',
            text: 'Opera is a fast and safe browser alternative. Learn how to use JXL images within Opera.'
        },
        {
            href: '/safari',
            name: 'Safari',
            side: 'no support',
            text: 'Is Safari adding JPEG XL support in 2022? Find out more about the current state.'
        },
        {
            href: '/vivaldi',
            name: 'Vivaldi',
            side: 'partial support',
            text: 'Vivaldi has JPEG XL support, but there are still some limitations. We show you how to use it.'
        }
    ]
    return (
        <>
            <Heading h='Safari JPEG XL support' p='#tutorials'/>
            <article>
                <nav className='content_table'>
                    <h4>Table of Content</h4>
                    <ol className='content_ol'>
                        <LiContent n='1' a='#what-is-jpeg-xl' text='What is JPEG XL?'/>
                        <LiContent n='2' a='#what-is-safari' text='What is Safari?'/>
                        <LiContent n='3' a='#webkit-support' text='Webkit Support'/>
                        <LiContent n='4' a='#why-it-might-take-longer' text='Why it might take longer'/>
                    </ol>
                </nav>
                <br/>
                <Line/>

                <span id='what-is-jpeg-xl'>
                    <h1 className="main">What is JPEG XL?</h1>
                    <p className='umain'>Compared to the original JPEG, JPEG XL offers 60% image size savings at the same perceptual quality while supporting modern features like HDR, animation, alpha channel, lossless JPEG recompression, lossless and progressive modes.</p>
                    <p className='umain'>JPEG XL offers significantly better image quality and compression ratio compared to legacy JPEG. Through lossless JPEG recompression, it is possible to convert JPEGs from the past to the new format quickly.</p>
                    <p className='umain'>JPEG XL supports both lossless and lossy compression. Lossless compression reduces filesizes by 20-60%. Using software implementations, encoding and decoding can be performed with a high degree of computational efficiency without hardware acceleration.</p>
                    <p className='umain'>There are several reasons to use JPEG XL as an image format for the web, including its support for HDR and animation, its small file size and progressive decoding mechanisms, and the ecosystem interest from web developers, CDNs, and large tech companies like Google and Facebook.</p>
                </span>
                <Line/>

                <span id='what-is-safari'>
                    <h1 className="main">What is Safari?</h1>
                    <p className='umain'>Safari is Apple's web browser, exclusive to iPhone, iPad, and macOS. It was first released in 2003. Safari's popularity grew with the iPhone and iPad, and it currently has about an 18% share of the browser market. Safari works like any other popular browser. Websites can be browsed, favorited, and multiple tabs can be opened at once. Apple's Safari was the first web browser to support HTML 5 while built with the WebKit engine. Safari has several excellent features, including automatic syncing between devices using the same iCloud account, as well as a built-in airdrop button that allows users to share a website quickly. The browser is exclusively available on Apple devices.</p>
                </span>
                <Line/>

                <span id='webkit-support'>
                    <h1 className="main">Webkit Support</h1>
                    <p className='umain'>Safari's core component, Webkit, has excellent news in the bug tracker. All iOS web browsers make use of this engine, including Apple's Safari web browser. WebKit is also used by BlackBerry browsers, PlayStation consoles, the browser part of Amazon's Kindle e-book reader, and Tizen mobile operating systems.</p>
                    <p className='umain'>A parent bug ticket tracks all tasks left for a full JPEG XL implementation. Tasks/Bugs:</p>
                    <p className='umain'>&#10004; Implement JPEG XL image decoder using libjxl (<span
                        className='ugreen'>DONE</span>)</p>
                    <p className='umain'>&#10004; JPEG-XL support at build time (<span className='ugreen'>DONE</span>)</p>
                    <p className='umain'>&#10004; JPEG XL decoder color profile support (<span
                        className='ugreen'>UNRESOLVED</span>)</p>
                    <p className='umain'>&#10004; Support Animated JPEG-XL images (<span className='ugreen'>UNRESOLVED</span>)</p>
                    <p className='umain'>When the unresolved tasks on Webkit are resolved, we can expect full support. There is a significant downside, however. Apple ports do not use WebKit's image decoding functions.</p>
                </span>
                <Line/>

                <span id='why-it-might-take-longer'>
                    <h1 className="main">Why it might take longer</h1>
                    <p className='umain'>Due to the lack of support for JPEG XL on iOS, iPadOS, and macOS, Safari does not support it.</p>
                    <p className='umain'>Remember: Apple did not add support for WebP till a decade after it was released. Safari is one of the most popular web browsers, so its refusal to add support for the format has delayed its adoption as a whole. Developers couldn't use this format without sacrificing Apple users' usability.</p>
                    <p className='umain'>With WebKit contributors having already begun supporting JPEG XL, one wonders when Apple will enable support. Apple implements its new formats in its operating system and not in its browsers. While JPEG XL support is about to be available in the WebKitGTK and WPE ports, it will remain unavailable in the Apple Safari ports. Apple allows Safari to interact with the operating system to determine which file formats to support. The browser does not decode images. The operating system does it.</p>
                    <p className='umain'>Apple software engineer Sam Sneddon, working for WebKit, confirmed this with his statement in the WebKit bug tracker:</p>
                    <div className='words'>
                        <p className='umain'>The Apple ports use the system-provided image decoding capabilities; we're unlikely to deviate from that here, so any support for [..] depends on underlying OS support.</p>
                        <p className='green2'>Sam Sneddon</p>
                    </div>
                    <br/>
                    <p className='umain'>Since Apple only releases OS and Safari updates twice a year, and there is no official announcement or alpha/beta operating system version that suggests JPEG XL support, <b>we will have to wait at least six months for Safari to support JPEG XL.</b></p>
                </span>
                <Line/>

                <br/><FAQComponent/><br/>
                <RelatedT related={related}/>
            </article>


            <ConvertButton/>
            <br/>
        </>)
}

export default Safari